/**
 * AuthErrorScreen
 * Handles OAuth errors and displays user-friendly error messages.
 */

import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useRoute, useNavigation, CommonActions } from '@react-navigation/native';
import { colors } from '../theme/colors';
import { openWhatsAppSupport } from '../utils/whatsapp';
import logger from '../utils/logger';

const ERROR_MESSAGES: Record<string, string> = {
  magic_link_expired: 'Ce lien de connexion a expiré ou a déjà été utilisé. Demande un nouveau lien.',
  access_denied: 'Tu as annulé la connexion avec Google.',
  token_exchange_failed: 'Erreur lors de la connexion avec Google. Réessaye.',
  user_info_failed: 'Impossible de récupérer tes informations Google.',
  user_creation_failed: 'Erreur lors de la création de ton compte.',
  default: 'Une erreur est survenue lors de la connexion.',
};

export default function AuthErrorScreen() {
  const route = useRoute();
  const navigation = useNavigation();

  const params = (route.params || {}) as Record<string, string>;
  const errorCode = params.error || 'default';
  const errorMessage = ERROR_MESSAGES[errorCode] || ERROR_MESSAGES.default;

  logger.error('[AuthErrorScreen] Error:', errorCode);

  const handleRetry = () => {
    navigation.dispatch(
      CommonActions.reset({ index: 0, routes: [{ name: 'Auth' }] })
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.iconContainer}>
        <Text style={styles.icon}>!</Text>
      </View>
      <Text style={styles.title}>Erreur de connexion</Text>
      <Text style={styles.message}>{errorMessage}</Text>
      <TouchableOpacity style={styles.button} onPress={handleRetry}>
        <Text style={styles.buttonText}>Réessayer</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={styles.whatsappButton}
        onPress={() => openWhatsAppSupport(`Erreur connexion : ${errorCode}`)}
      >
        <Text style={styles.whatsappButtonText}>Contacter le support</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.background,
    padding: 24,
  },
  iconContainer: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: colors.error || '#EF4444',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
  },
  icon: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: colors.text,
    marginBottom: 12,
    textAlign: 'center',
  },
  message: {
    fontSize: 16,
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: 32,
    lineHeight: 24,
  },
  button: {
    backgroundColor: colors.primary,
    paddingHorizontal: 32,
    paddingVertical: 14,
    borderRadius: 24,
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  whatsappButton: {
    marginTop: 16,
    paddingHorizontal: 32,
    paddingVertical: 12,
  },
  whatsappButtonText: {
    color: '#25D366',
    fontSize: 15,
    fontWeight: '600',
  },
});
